import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty } from "lodash";

import { selectCommunityLocation } from "store/community/selectors";

import { useStateSelector } from "utils/field";
import { requiredId, getValidation, getDecimalPlaces } from "utils/fieldValidation";
import { getOptions, getBaseUnits, getUnitOptions } from "utils/fields";
import {
    getDefaultF280Option,
    getF280WeatherOpts,
    getF280DesignHeatingTemp,
    getF280DesignCoolingTemp,
} from "utils/weather";

import Field from "components/Field";
import InputRow from "components/Input/Row";
import Input from "components/Input";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Button from "components/Button";
import InfoTooltip from "components/InfoTooltip";

import classes from "../../style.module.scss";

const baseRegionValidation = getValidation("weatherRegion");
const v11_10_RegionValidation = getValidation("weatherRegion", "v11-10");
const baseLocationValidation = getValidation("weatherLocation");
const v11_10_locationValidation = getValidation("weatherLocation", "v11-10");
const wthLibValidation = getValidation("weatherLibrary");
const depthOfFrostValidation = getValidation("depthOfFrostline");

const WeatherLocationInfo = ({
    change,
    modelUnits = "Metric",
    h2kVersion = "v11-12",
    programClass = "",
    f280Warning,
    communityLocation,
}) => {
    const accessor = `community.community.communityDetails.weatherLocation`;

    const { id: weatherProvTerrId } = useStateSelector(`${accessor}.location.weather.region`) || {};
    const { id: f280LocationId } = useStateSelector(`${accessor}.location.weather.f280.location`) || {};
    const depthOfFrostUnits = useStateSelector(`${accessor}.location.weather.depthOfFrostline_u`);

    const regionValidation = h2kVersion === "v11-9" ? baseRegionValidation : v11_10_RegionValidation;
    const locationValidation = h2kVersion === "v11-9" ? baseLocationValidation : v11_10_locationValidation;

    let weatherLibOpts = [{}];
    if (h2kVersion === "v11-9") {
        weatherLibOpts = [
            {
                value: "Wth110.dir",
                label: "Wth110.dir",
            },
        ];
    } else {
        //H2k version > 11.9
        weatherLibOpts = [
            {
                value: "Wth2020.dir",
                label: "Wth2020.dir",
            },
        ];
    }

    const [useCommunityWeather, setUseCommunityWeather] = useState(false);

    useEffect(() => {
        if (!isEmpty(communityLocation)) {
            setUseCommunityWeather(true);
        } else {
            setUseCommunityWeather(false);
        }
    }, [communityLocation]);

    return (
        <React.Fragment>
            <div className={classes.locationInfo}>
                <h3>Weather and Location Information</h3>
                <InfoTooltip
                    className={classes.info}
                    info={
                        "Run all models with the same weather region by defining the fields in the section below. Otherwise, leave it blank (Clear Data)."
                    }
                />
            </div>
            <InputRow gridTemplate="1fr 1fr 1fr">
                <Field
                    component={Select}
                    name={`${accessor}.location.weather.region`}
                    options={getOptions({ fieldKey: "weatherRegion", version: h2kVersion })}
                    label="Province or Territory"
                    placeholder="Choose Province or Territory"
                    validate={useCommunityWeather ? [requiredId, ...regionValidation] : [...regionValidation]}
                    validationTrigger={useCommunityWeather}
                    search={true}
                    onChange={() => {
                        change(`${accessor}.location.weather.location`, {});
                        change(`${accessor}.location.weather.library`, weatherLibOpts[0].value);
                    }}
                    warning={programClass.includes("ONrh") && weatherProvTerrId !== 5 ? "orhModeLocationMismatch" : ""}
                />
                <Field
                    component={Select}
                    name={`${accessor}.location.weather.location`}
                    options={getOptions({
                        fieldKey: "weatherLocation",
                        indKey: weatherProvTerrId,
                        version: h2kVersion,
                    })}
                    label="Location"
                    placeholder="Choose Location"
                    validate={useCommunityWeather ? [requiredId, ...locationValidation] : [...locationValidation]}
                    validationTrigger={useCommunityWeather}
                    search={true}
                    onChange={({ id }) => {
                        change(`${accessor}.location.weather.f280.location.id`, getDefaultF280Option(id));
                    }}
                />
                <Field
                    component={Input}
                    type="number"
                    name={`${accessor}.location.weather.location.value`}
                    label="Heating Degree Days"
                    placeholder={0.0}
                    validate={locationValidation}
                    decimals={getDecimalPlaces("weatherLocation")}
                    disabled={true}
                />
            </InputRow>
            <InputRow gridTemplate="66% 31.85%">
                <Field
                    component={Select}
                    name={`${accessor}.location.weather.library`}
                    options={weatherLibOpts}
                    label="Weather Library"
                    placeholder="Choose Weather Library"
                    validate={wthLibValidation}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.location.weather.depthOfFrostline`}
                    label="Depth of Frostline"
                    placeholder="0.00"
                    validate={depthOfFrostValidation}
                    decimals={getDecimalPlaces("depthOfFrostline")}
                    change={change}
                    units={{
                        base: getBaseUnits("depthOfFrostline", modelUnits),
                        options: getUnitOptions("length"),
                        selected: depthOfFrostUnits,
                        unitType: "length",
                        accessor: `${accessor}.location.weather.depthOfFrostline_u`,
                    }}
                />
            </InputRow>
            <InputRow gridTemplate="1fr 1fr 1fr">
                <Field
                    component={Select}
                    type="number"
                    name={`${accessor}.location.weather.f280.location`}
                    label="CSA F280-12 Design Conditions"
                    placeholder={"Select a F280 Design Conditions"}
                    options={getF280WeatherOpts(weatherProvTerrId)}
                    nonH2kParam="f280Weather"
                    search={true}
                    warning={f280Warning}
                    onChange={() => {
                        change(`${accessor}.location.weather.f280.location_warning`, "");
                    }}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.location.weather.f280.dhdbt`}
                    label="Heating Design Temperature"
                    placeholder={0.0}
                    setValue={getF280DesignHeatingTemp(f280LocationId)}
                    decimals={getDecimalPlaces("weatherLocation")}
                    disabled={true}
                    units={{
                        base: getBaseUnits("ashpTempCutoff", modelUnits),
                        options: getUnitOptions("temperature"),
                        selected: "C",
                        unitType: "temperature",
                        accessor: `${accessor}.location.weather.f280.dhdbt_u`,
                    }}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.location.weather.f280.dcdbt`}
                    label="Cooling Design Temperature"
                    placeholder={0.0}
                    setValue={getF280DesignCoolingTemp(f280LocationId)}
                    decimals={getDecimalPlaces("weatherLocation")}
                    disabled={true}
                    units={{
                        base: getBaseUnits("ashpTempCutoff", modelUnits),
                        options: getUnitOptions("temperature"),
                        selected: "C",
                        unitType: "temperature",
                        accessor: `${accessor}.location.weather.f280.dcdbt_u`,
                    }}
                />
            </InputRow>
            <div className={classes.buttons}>
                <Button
                    disabled={!useCommunityWeather}
                    onClick={() => {
                        change(`${accessor}`, {});
                    }}
                >
                    Clear Data
                </Button>
            </div>
        </React.Fragment>
    );
};

const mapStateToProps = createStructuredSelector({
    communityLocation: selectCommunityLocation,
});

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(WeatherLocationInfo);
